const { h } = window.preact;
const { useState, useRef, useEffect } = window.preactHooks;
const html = window.htm.bind(h);

import { useRollenfinder } from "./rollenfinder-context.js";
import { Spinner } from "./Spinner.js";
import { ProductCard } from "./ProductCard.js";
import { getTranslation } from "./translations.js";

async function fetchProducts(printerModelHandle) {
  try {
    if (!printerModelHandle) {
      return [];
    }

    const response = await fetch(
      `/apps/rollenfinder/fitting-products/${printerModelHandle}`,
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Transform the data to match the expected format for ProductCard
    return data.compatibleProducts
      .map((product) => {
        try {
          // Clean up variant ID by removing the Shopify prefix
          const variantId = product.variant?.id
            ? String(product.variant.id).replace(
                "gid://shopify/ProductVariant/",
                "",
              )
            : "";

          // Clean up product ID by removing the Shopify prefix
          const productId = String(product.id).replace(
            "gid://shopify/Product/",
            "",
          );

          // Sanitize text fields to remove any potential invalid characters
          const sanitizeText = (text) => {
            if (!text) return "";
            // Keep UTF-8 characters but remove control characters
            return String(text)
              .replace(/[\u0000-\u001F\u007F-\u009F]/g, "") // Remove control characters
              .replace(/\u00A0/g, " ") // Replace non-breaking space with regular space
              .trim();
          };

          // Ensure HTML is properly encoded
          const sanitizeHtml = (html) => {
            if (!html) return "";
            return sanitizeText(html)
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")
              .replace(/"/g, "&quot;")
              .replace(/'/g, "&#039;");
          };

          const transformedProduct = {
            id: productId,
            handle: sanitizeText(product.handle),
            title: sanitizeText(product.title),
            description: sanitizeHtml(product.descriptionHtml),
            available:
              !product.hasOutOfStockVariants &&
              product.variant?.availableForSale !== false,
            price: product.price?.amount
              ? Math.round(parseFloat(product.price.amount) * 100)
              : 0,
            compare_at_price: product.compareAtPrice?.amount
              ? Math.round(parseFloat(product.compareAtPrice.amount) * 100)
              : null,
            images: Array.isArray(product.images)
              ? product.images.map((img) => ({
                  url: sanitizeText(img.url),
                  altText: sanitizeText(img.altText || ""),
                }))
              : [],
            featured_image: product.images?.[0]?.url
              ? sanitizeText(product.images[0].url)
              : "",
            variants: [
              {
                id: variantId,
                price: product.variant?.price
                  ? Math.round(parseFloat(product.variant.price) * 100)
                  : product.price?.amount
                    ? Math.round(parseFloat(product.price.amount) * 100)
                    : 0,
                compare_at_price: product.variant?.compareAtPrice
                  ? Math.round(parseFloat(product.variant.compareAtPrice) * 100)
                  : product.compareAtPrice?.amount
                    ? Math.round(
                        parseFloat(product.compareAtPrice.amount) * 100,
                      )
                    : null,
                available:
                  product.variant?.availableForSale ??
                  !product.hasOutOfStockVariants,
                inventory_quantity: product.variant?.quantityAvailable || 0,
              },
            ],
            metafields: {
              sepa_lastschrift_aufdruck:
                product.customMetafields?.sepa_lastschrift_aufdruck ===
                  "true" ||
                product.customMetafields?.sepa_lastschrift_aufdruck === true,
              besonderheit: sanitizeText(
                product.customMetafields?.besonderheit || "",
              ),
              rollen_pro_karton: String(
                product.customMetafields?.rollen_pro_karton || "0",
              ),
              second_product_img: sanitizeText(
                product.customMetafields?.second_product_img || "",
              ),
              rollenbreite: sanitizeText(
                product.dimensions?.rollenbreite || "",
              ),
              durchmesser_rolle: sanitizeText(
                product.dimensions?.durchmesser_rolle || "",
              ),
              rollenlange: sanitizeText(product.dimensions?.rollenlange || ""),
              isBlue4est: Boolean(product.isBlue4est),
            },
          };

          return transformedProduct;
        } catch (error) {
          console.error("Error transforming product:", product, error);
          return null;
        }
      })
      .filter(Boolean); // Remove any null products that failed to transform
  } catch (error) {
    console.error("Error fetching products:", error);
    return [];
  }
}

export function RollenfinderStep3() {
  const { state, updateState } = useRollenfinder();
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState([]);

  // Fetch products when component mounts
  useEffect(() => {
    const loadProducts = async () => {
      if (!state.selectedModel?.handle) {
        return;
      }

      setIsLoading(true);
      try {
        const fetchedProducts = await fetchProducts(state.selectedModel.handle);
        setProducts(fetchedProducts);
        updateState({ fittingProducts: fetchedProducts });
      } catch (error) {
        console.error("Error loading products:", error);
        setProducts([]);
        updateState({ fittingProducts: [] });
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, [state.selectedModel]);

  if (isLoading) {
    return html`<${Spinner} size="lg" />`;
  }

  return html`
    <div class="flex flex-col gap-6">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-bold md:text-2xl">
          ${getTranslation("steps.step_3.products_found", {
            count: products.length,
          })}
        </h2>
      </div>

      <div class="flex gap-6">
        <div class="flex-1">
          ${products.length === 0
            ? html`
                <div
                  class="flex flex-col items-center justify-center rounded-lg p-4 md:p-8"
                >
                  <div class="text-center">
                    <svg
                      class="mx-auto h-8 w-8 text-gray-400 md:h-12 md:w-12"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <h3
                      class="mt-2 text-sm font-medium text-gray-900 md:text-base"
                    >
                      ${getTranslation("steps.step_3.no_products.title")}
                    </h3>
                    <p class="mt-1 text-xs text-gray-500 md:text-sm">
                      ${getTranslation("steps.step_3.no_products.description")}
                    </p>
                    <p
                      class="mt-4 text-xs text-gray-700 md:text-sm"
                      dangerouslySetInnerHTML=${getTranslation(
                        "steps.step_3.no_products.phone_help",
                      )}
                    ></p>
                  </div>
                </div>
              `
            : html`
                <div
                  class="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3"
                >
                  ${products.map(
                    (product) => html`
                      <${ProductCard} key=${product.id} product=${product} />
                    `,
                  )}
                </div>
              `}
        </div>
      </div>
    </div>
  `;
}
